import React, { useState } from 'react';
import { Send, Phone, Mail, MapPin, Clock, CheckCircle2, Sparkles, Award, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Submit to backend API
      await axios.post(`${API}/contact`, formData);
      
      toast.success('Thank you for your inquiry! We\'ll be in touch within 24 hours.', {
        description: 'Your message has been sent to tamrakarbablee@gmail.com'
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: companyInfo.contact.phone,
      link: `tel:${companyInfo.contact.phone.replace(/\s/g, '')}`
    },
    {
      icon: Mail,
      title: 'Email',
      value: companyInfo.contact.email,
      link: `mailto:${companyInfo.contact.email}`
    },
    {
      icon: MapPin,
      title: 'Location',
      value: companyInfo.contact.address,
      link: '#'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      value: 'Mon - Fri: 8am - 6pm',
      link: '#'
    }
  ];

  const services = [
    'Hotel Staffing & Recruitment',
    'Housekeeping Services',
    'Hospitality Training',
    'Management Consulting',
    'Other'
  ];

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
      {/* Premium Background Elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-shsTeal/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-shsBlue/5 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-0 w-72 h-72 bg-shsGold/5 rounded-full blur-3xl" />
      
      {/* Decorative Elements */}
      <div className="absolute top-20 right-20 w-4 h-4 bg-shsTeal rounded-full animate-pulse" />
      <div className="absolute bottom-40 left-20 w-3 h-3 bg-shsGold rounded-full animate-pulse delay-300" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-gradient-to-r from-shsTeal/10 to-shsBlue/10 text-shsTeal rounded-full text-sm font-semibold mb-4 border border-shsTeal/20">
            Get In Touch
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-shsNavy mb-6">
            Let's Elevate Your
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-shsTeal to-shsBlue"> Guest Experience</span>
          </h2>
          <p className="text-gray-600 text-lg">
            Ready to transform your hospitality operations? Contact us today for a
            consultation.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information Card */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl shadow-shsTeal/10 border border-gray-100 relative overflow-hidden">
              {/* Decorative Corner */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-shsTeal/10 to-shsBlue/10 rounded-full" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-shsTeal/10 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-shsTeal" />
                  </div>
                  <h3 className="text-xl font-bold text-shsNavy">Contact Information</h3>
                </div>
                
                <div className="space-y-5">
                  {contactInfo.map((item, index) => (
                    <a
                      key={index}
                      href={item.link}
                      className="flex items-start gap-4 group p-3 -mx-3 rounded-xl hover:bg-shsTeal/5 transition-colors"
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-shsTeal/10 to-shsBlue/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:from-shsTeal group-hover:to-shsBlue transition-all duration-300">
                        <item.icon className="w-5 h-5 text-shsTeal group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{item.title}</p>
                        <p className="font-semibold text-shsNavy group-hover:text-shsTeal transition-colors">
                          {item.value}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Why Partner With Us - Premium */}
            <div className="bg-gradient-to-br from-shsNavy via-shsBlue to-shsNavy rounded-3xl p-8 text-white relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
              </div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-shsGold/20 rounded-xl flex items-center justify-center">
                    <Award className="w-5 h-5 text-shsGold" />
                  </div>
                  <h3 className="text-xl font-bold">Why Partner With Us?</h3>
                </div>
                
                <div className="space-y-4">
                  {[
                    { text: 'Free initial consultation', icon: CheckCircle2 },
                    { text: 'Tailored solutions for your property', icon: CheckCircle2 },
                    { text: 'Quick response within 24 hours', icon: CheckCircle2 },
                    { text: 'No long-term contracts required', icon: CheckCircle2 }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3 group">
                      <div className="w-6 h-6 bg-shsGold/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-shsGold transition-colors">
                        <item.icon className="w-4 h-4 text-shsGold group-hover:text-shsNavy transition-colors" />
                      </div>
                      <span className="text-white/90 group-hover:text-white transition-colors">{item.text}</span>
                    </div>
                  ))}
                </div>

                {/* Trust Badge */}
                <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-3">
                  <Shield className="w-8 h-8 text-shsGold" />
                  <div>
                    <p className="font-semibold text-sm">100% Satisfaction Guaranteed</p>
                    <p className="text-xs text-white/60">Your success is our priority</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form - Premium */}
          <div className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl p-8 lg:p-10 shadow-2xl shadow-shsTeal/10 border border-gray-100 relative overflow-hidden"
            >
              {/* Form Header Accent */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-shsTeal via-shsBlue to-shsGold" />
              
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-shsTeal to-shsBlue rounded-xl flex items-center justify-center">
                  <Send className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-shsNavy">Send Us a Message</h3>
                  <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Smith"
                    className="rounded-xl border-gray-200 focus:border-shsTeal focus:ring-shsTeal py-6 transition-shadow hover:shadow-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@hotel.com"
                    className="rounded-xl border-gray-200 focus:border-shsTeal focus:ring-shsTeal py-6 transition-shadow hover:shadow-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+61 400 000 000"
                    className="rounded-xl border-gray-200 focus:border-shsTeal focus:ring-shsTeal py-6 transition-shadow hover:shadow-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Company/Property
                  </label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Hotel Name"
                    className="rounded-xl border-gray-200 focus:border-shsTeal focus:ring-shsTeal py-6 transition-shadow hover:shadow-md"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Service Interested In
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-200 focus:border-shsTeal focus:ring-shsTeal py-3.5 px-4 text-gray-700 transition-shadow hover:shadow-md"
                >
                  <option value="">Select a service</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell us about your hospitality needs..."
                  className="rounded-xl border-gray-200 focus:border-shsTeal focus:ring-shsTeal resize-none transition-shadow hover:shadow-md"
                />
              </div>

              <div className="mt-8">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-shsTeal to-shsBlue hover:from-shsBlue hover:to-shsNavy text-white py-6 rounded-full text-lg font-bold transition-all duration-300 hover:shadow-xl hover:shadow-shsTeal/30 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02]"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending Message...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Send Message
                      <Send className="w-5 h-5" />
                    </span>
                  )}
                </Button>
                
                <p className="text-center text-xs text-gray-400 mt-4">
                  By submitting, you agree to be contacted about our services.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
